import { useRef, useState } from 'react'
import { ErrorBanner, useAsync } from '../../components/Async'
import { usePreferences } from '../../lib/PreferencesContext'
import {
  WIDGET_SIZES,
  type WidgetChoice,
  moveWidget,
  resolveLayout,
  sizesFor,
  updateWidget
} from '../../lib/preferences'
import { ApiError, alerts, coverage as coverageApi, insights } from '../../lib/api'
import { PageTitle } from '../../components/PageTitle'
import { AddWidgetMenu } from './AddWidgetMenu'
import { WidgetCard } from './WidgetCard'
import { type DropZone, type WidgetBox, applyDrop, sameArrangement, widgetAt, zoneFor } from './dragOrder'
import { WIDGETS, WIDGET_BY_ID } from './widgets'

/**
 * What is outstanding, above what the fleet has been doing.
 *
 * The Board is the Dashboard reordered around a different question. A dashboard answers "what has the
 * fleet been doing"; an operator opening this screen needs "what must I decide now", and the ribbon
 * at the top is that answer - three things that are outstanding and three that are merely true.
 * Everything below it is the same arrangement of read models it always was.
 *
 * Read models rather than aggregate queries: a board that recomputes on every page load is one that
 * gets slower with the data it is describing, and this one is read most often on the day there is
 * most of it. The ribbon's three problem figures are the exception and are read live, because the
 * coverage gap has to be readable when a projection is stale - which is the whole reason it is not
 * a projection.
 *
 * The page itself no longer decides what is on it. It loads the four read models once and hands them
 * to whichever widgets the operator has switched on, in the order they put them — because the useful
 * arrangement genuinely differs by job. Somebody triaging wants detections and severity; somebody
 * rolling out policy wants coverage by subnet; and a fixed page has to disappoint one of them.
 */
export function DashboardPage() {
  const [days, setDays] = useState(14)
  const activity = useAsync(() => insights.activity(days), [days])
  const detections = useAsync(() => insights.detections(days), [days])
  const topology = useAsync(() => insights.topology(), [])
  const projections = useAsync(() => insights.projections(), [])
  // The ribbon's three problem tiles. Read live rather than from a projection, for the reason the
  // coverage figures are: the gap has to be readable when a chart is stale.
  const coverage = useAsync(() => coverageApi.get(), [])
  const destinations = useAsync(() => insights.destinations(days), [days])
  const openCases = useAsync(() => alerts.list({ state: 'new' }), [])

  const { dashboardLayout, setDashboardLayout, loading: preferencesLoading } = usePreferences()
  const [customising, setCustomising] = useState(false)

  /**
   * The arrangement being dragged, before it is committed.
   *
   * `setDashboardLayout` writes to the server on every call, so reordering straight into it would be
   * one request per pointer move. The drag reorders this instead, the board renders from it, and the
   * save happens once on release.
   */
  const [drafted, setDrafted] = useState<WidgetChoice[] | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  /** What the pointer is currently over, so the card under it can show where the drop would land. */
  const [hint, setHint] = useState<{ widget: string; zone: DropZone } | null>(null)
  const board = useRef<HTMLDivElement>(null)

  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function run(work: () => Promise<string>) {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      setNotice(await work())
      topology.reload()
      projections.reload()
      activity.reload()
      detections.reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The request failed.')
    } finally {
      setBusy(false)
    }
  }

  const data = {
    // Null rather than an empty array when a read has not arrived. Coercing here is what let the tiles
    // report an expired session as a fleet with nothing wrong with it (D-263); the widgets now say
    // which of the two they are looking at.
    activity: activity.value,
    detections: detections.value,
    topology: topology.value,
    coverage: coverage.value,
    destinations: destinations.value,
    // The count, not the page. `total` is every open case; `items` is the first fifty of them, and a
    // tile reading "50 need a decision" when there are four hundred would be worse than a dash.
    openCases: openCases.value?.total ?? null,
    projections: projections.value ?? [],
    windowDays: days,
    busy,
    rebuildTopology: () =>
      run(async () => `Topology rebuilt for ${(await insights.rebuildTopology()).devices} device(s).`),
    rebuild: (projection: string) =>
      run(async () => `${projection} rebuilt: ${(await insights.rebuild(projection)).rows} row(s).`)
  }

  // Held back until the account's arrangement has arrived, so the page does not draw the defaults and
  // then rearrange itself under somebody who has already started reading it.
  const stored = preferencesLoading ? [] : resolveLayout(dashboardLayout, WIDGETS)
  const layout = drafted ?? stored
  const shown = layout.filter((choice) => choice.visible)

  // In catalog order rather than layout order: this is a list of things that are not on the board,
  // so the order they were switched off in says nothing useful about them.
  const hidden = WIDGETS.filter((definition) =>
    layout.some((choice) => choice.widget === definition.id && !choice.visible)
  )

  const change = (widget: string, edit: Partial<Omit<WidgetChoice, 'widget'>>) =>
    setDashboardLayout(updateWidget(layout, widget, edit))

  const move = (widget: string, direction: -1 | 1) =>
    setDashboardLayout(moveWidget(layout, widget, direction))

  /**
   * Puts a widget back on the board, at the end.
   *
   * At the end rather than wherever it used to sit: a panel reappearing in the middle of an
   * arrangement somebody has made moves everything below it, and the end is where they will look for
   * the thing they just added. It comes back at whatever width it was last given.
   */
  const add = (widget: string) =>
    setDashboardLayout([
      ...layout.filter((choice) => choice.widget !== widget),
      { ...layout.find((choice) => choice.widget === widget)!, visible: true }
    ])

  /** What the catalog will let a widget be drawn at, which decides whether a drop can share a row. */
  const allowed = (widget: string) => {
    const definition = WIDGET_BY_ID.get(widget)
    return definition ? sizesFor(definition) : WIDGET_SIZES
  }

  /** Every visible card's rectangle in page coordinates, measured at the moment it is needed. */
  const boxes = (): WidgetBox[] => {
    const cards = board.current?.querySelectorAll<HTMLElement>('[data-widget]') ?? []
    return [...cards].map((card) => {
      const rectangle = card.getBoundingClientRect()
      return {
        widget: card.dataset.widget!,
        left: rectangle.left + window.scrollX,
        top: rectangle.top + window.scrollY,
        width: rectangle.width,
        height: rectangle.height
      }
    })
  }

  /**
   * Arranges while the pointer moves, and saves once when it is let go.
   *
   * The drop is applied against the layout as it was when the drag started, not against the running
   * draft. Compounding each move onto the last would make a drag across three cards apply three
   * moves, and a pointer wandering back and forth over one boundary would ratchet the widths narrower
   * every time it crossed. Recomputing from a fixed starting point means what is on screen is always
   * exactly what releasing here would save.
   *
   * The listeners go on the window rather than on the grip, and that is what makes this work at all.
   * Pointer capture on the grip looks like the right tool and is not: the cards reorder constantly
   * during a drag - the thing being dragged is the thing moving - and React repositioning the grip in
   * the DOM drops the capture. The release then lands on whatever the pointer happens to be over, the
   * drag never ends, and the new arrangement is shown but never saved. On the window there is nothing
   * to lose.
   */
  const startDrag = (widget: string) => (event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault()
    setDragging(widget)

    const started = layout
    let arrangement = layout

    const onMove = (moved: PointerEvent) => {
      const over = widgetAt(boxes(), moved.pageX, moved.pageY)
      if (!over || over.widget === widget) {
        setHint(null)
        return
      }

      const zone = zoneFor(over, moved.pageX)
      setHint({ widget: over.widget, zone })

      arrangement = applyDrop(started, widget, over.widget, zone, allowed)
      setDrafted(arrangement)
    }

    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      setDragging(null)
      setDrafted(null)
      setHint(null)

      // Nothing is written for a drag that put everything back where it started, which is what a
      // click on the grip amounts to. Widths count as well as order: dropping a panel beside another
      // changes both, and a comparison on order alone would silently discard that.
      if (!sameArrangement(arrangement, started)) setDashboardLayout(arrangement)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Board</PageTitle>
          <p>What needs a decision, and what the fleet has been doing.</p>
        </div>
        <div className="toolbar">
          <select
            aria-label="Period"
            style={{ width: 'auto' }}
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            data-testid="dashboard-period"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button
            type="button"
            aria-expanded={customising}
            onClick={() => setCustomising((open) => !open)}
            data-testid="customise-dashboard"
          >
            {customising ? 'Done' : 'Customise'}
          </button>
        </div>
      </div>

      <ErrorBanner message={failure ?? activity.error ?? detections.error ?? topology.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="dashboard-notice">
          {notice}
        </div>
      )}

      {/*
        A strip, where there used to be a card holding a list of every widget in the catalog.

        The list was a second copy of the board, above the board: to widen a panel somebody found its
        row among eleven, changed a select, and then looked down the page to see what had happened.
        Every one of those controls is now on the panel it belongs to, so all that is left here is the
        sentence saying whose arrangement this is and the one thing a panel cannot do for itself -
        come back once it has been hidden.
      */}
      {customising && (
        <div className="banner customising-bar" data-testid="customising-bar">
          <span>
            Drag a panel by its grip to move it, or onto the left or right edge of another to set them
            side by side. This arrangement is yours alone.
          </span>
          <AddWidgetMenu hidden={hidden} onAdd={add} />
        </div>
      )}

      {/*
        A grid rather than a stack of full-width cards. Half of what this board draws is a ranking of
        eight rows or a donut, and a donut across fourteen hundred pixels is a donut with an acre of
        nothing beside it - so an operator could arrange the order of their panels and not the shape
        of the screen. It collapses to one column on a phone, where side by side means neither is
        readable.
      */}
      <div className={customising ? 'board-grid customising' : 'board-grid'} ref={board}>
        {shown.map((choice, index) => {
          const widget = WIDGET_BY_ID.get(choice.widget)
          if (!widget) return null

          return (
            <WidgetCard
              key={choice.widget}
              widget={widget}
              choice={choice}
              index={index}
              count={shown.length}
              data={data}
              customising={customising}
              dragging={dragging === choice.widget}
              hint={hint?.widget === choice.widget ? hint.zone : null}
              onMove={(direction) => move(choice.widget, direction)}
              onChange={(edit) => change(choice.widget, edit)}
              onDragStart={startDrag(choice.widget)}
            />
          )
        })}
      </div>

      {!preferencesLoading && shown.length === 0 && (
        <div className="card">
          <div className="card-body empty">
            {customising
              ? 'Every panel is switched off. Add one from the menu above.'
              : 'Every panel is switched off. Customise puts them back.'}
          </div>
        </div>
      )}
    </>
  )
}
